import { Card, CardContent } from "components/ui/card"
import { Badge } from "components/ui/badge"
import { Checkbox } from "components/ui/checkbox"
import { Button } from "components/ui/button"
import { Textarea } from "components/ui/textarea"
import { ChevronDown, ChevronUp, History, Info } from "lucide-react"
import { formatToBRL } from "@/utils/formatToMoney"
import { formatOperatorName } from "@/utils/format-operator-name"
import { getTimeAgo } from "@/utils/time-ago"
import { getStatusVariant, getStatusLabel, ReviewStatus } from "../../types/shared"
import type { CombinedItem } from "../../utils/item-helpers"
import {
  getItemLastUpdate,
  isItemAlreadyReviewed,
  canItemBeSelected,
  canShowHistory,
  getItemDisplayName,
  getItemTypeLabel
} from "../../utils/item-helpers"
import { FileThumbnail } from "../shared"
import type { DivergenceFile } from "../../types"

interface ReviewItemCardProps {
  item: CombinedItem
  isSelected: boolean
  isExpanded: boolean
  reviewNote: string
  onSelect: () => void
  onToggleExpanded: () => void
  onNoteChange: (value: string) => void
  onFileClick: (file: DivergenceFile) => void
  onShowHistory: () => void
  isLoading: boolean
}

const MAX_NOTE_LENGTH = 500

export const ReviewItemCard = ({
  item,
  isSelected,
  isExpanded,
  reviewNote,
  onSelect,
  onToggleExpanded,
  onNoteChange,
  onFileClick,
  onShowHistory,
  isLoading
}: ReviewItemCardProps) => {
  const alreadyReviewed = isItemAlreadyReviewed(item.review_status)
  const selectable = canItemBeSelected(item.review_status)
  const hasHistory = canShowHistory(item)

  const cardClassName = [
    isSelected && "border-blue-300 bg-blue-50",
    alreadyReviewed && "opacity-60"
  ].filter(Boolean).join(" ")

  return (
    <Card className={cardClassName}>
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={isSelected}
            onCheckedChange={onSelect}
            disabled={!selectable || isLoading}
            className="mt-0.5"
          />

          <div className="flex-1 min-w-0">
            <ItemHeader item={item} />
            <ItemMetadata item={item} />
            <ItemFiles files={item.files} onFileClick={onFileClick} />
            <ReviewedInfo item={item} alreadyReviewed={alreadyReviewed} />
            <PendingAnswerWarning status={item.review_status} />

            <ItemActions
              selectable={selectable}
              isExpanded={isExpanded}
              hasHistory={hasHistory}
              onToggleExpanded={onToggleExpanded}
              onShowHistory={onShowHistory}
              isLoading={isLoading}
            />

            {alreadyReviewed && item.review_notes && (
              <ReviewNotes notes={item.review_notes} />
            )}

            {isExpanded && (
              <IndividualNote
                value={reviewNote}
                onChange={onNoteChange}
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const ItemHeader = ({ item }: { item: CombinedItem }) => (
  <div className="flex items-center justify-between gap-2 mb-1">
    <div className="flex-1 min-w-0">
      <div className="font-medium text-sm truncate">
        {getItemDisplayName(item)}
      </div>
      <div className="text-xs text-gray-600">
        {getItemTypeLabel(item.type)} • {item.operator}
      </div>
    </div>

    <div className="flex items-center gap-2 flex-shrink-0">
      <Badge variant={getStatusVariant(item.review_status)} className="text-xs">
        {getStatusLabel(item.review_status)}
      </Badge>
      <span className="font-semibold text-sm whitespace-nowrap">
        {formatToBRL(item.amount)}
      </span>
    </div>
  </div>
)

const ItemMetadata = ({ item }: { item: CombinedItem }) => (
  <>
    <div className="text-xs text-gray-500 mt-1">
      Última solicitação faz {getTimeAgo(getItemLastUpdate(item))}
    </div>
    {item.store_justification && (
      <div className="text-xs text-gray-500 mt-1">
        <span className="font-medium">Justificativa:</span> {item.store_justification}
      </div>
    )}
  </>
)

const ItemFiles = ({
  files,
  onFileClick
}: {
  files?: any[]
  onFileClick: (file: DivergenceFile) => void
}) => {
  if (!files?.length) return null

  return (
    <div className="mt-2">
      <p className="text-xs text-gray-600 mb-1">Arquivos anexados: {files.length}</p>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {files.map((file: DivergenceFile) => (
          <FileThumbnail
            key={file.id}
            file={file}
            onClick={() => onFileClick(file)}
          />
        ))}
      </div>
    </div>
  )
}

const ReviewedInfo = ({
  item,
  alreadyReviewed
}: {
  item: CombinedItem
  alreadyReviewed: boolean
}) => {
  if (!alreadyReviewed) return null

  const reviewDate = item.reviewed_at
    ? new Date(item.reviewed_at).toLocaleDateString('pt-BR')
    : 'N/A'

  return (
    <div className="text-xs text-gray-400 mt-1 italic">
      ✓ Já revisado em {reviewDate}
      {item.reviewed_by && ` por ${formatOperatorName(item.reviewed_by)}`}
    </div>
  )
}

const PendingAnswerWarning = ({ status }: { status: ReviewStatus }) => {
  if (status !== ReviewStatus.PENDING_ANSWER) return null

  return (
    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
      <span className="font-medium flex items-center gap-1">
        <Info className="w-3.5 h-3.5" />
        Aguardando Resposta Inicial:
      </span>
      Este item está aguardando a primeira resposta da loja. Não pode ser reprovado até que a loja responda.
    </div>
  )
}

const ItemActions = ({
  selectable,
  isExpanded,
  hasHistory,
  onToggleExpanded,
  onShowHistory,
  isLoading
}: {
  selectable: boolean
  isExpanded: boolean
  hasHistory: boolean
  onToggleExpanded: () => void
  onShowHistory: () => void
  isLoading: boolean
}) => {
  if (!selectable && !hasHistory) return null

  return (
    <div className="flex gap-2 mt-1">
      {selectable && (
        <Button
          size="sm"
          onClick={onToggleExpanded}
          className="h-7 px-2 text-xs flex-1 justify-start"
          disabled={isLoading}
        >
          {isExpanded ? (
            <ChevronUp className="w-3 h-3 mr-1" />
          ) : (
            <ChevronDown className="w-3 h-3 mr-1" />
          )}
          {isExpanded ? "Ocultar" : "Adicionar"} observação individual
        </Button>
      )}

      {hasHistory && (
        <Button
          size="sm"
          variant="outline"
          onClick={onShowHistory}
          className="h-7 px-2 text-xs"
          disabled={isLoading}
        >
          <History className="w-3 h-3 mr-1" />
          Histórico
        </Button>
      )}
    </div>
  )
}

const ReviewNotes = ({ notes }: { notes: string }) => (
  <div className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-600">
    <span className="font-medium">Observação da revisão:</span><br />
    {notes}
  </div>
)

const IndividualNote = ({
  value,
  onChange
}: {
  value: string
  onChange: (value: string) => void
}) => (
  <div className="mt-2">
    <Textarea
      placeholder="Observação específica..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="min-h-[50px] resize-none text-sm"
      maxLength={MAX_NOTE_LENGTH}
    />
    <p className="text-xs text-gray-500 mt-1">{value.length}/{MAX_NOTE_LENGTH}</p>
  </div>
)
